import React from 'react';
import { i18n } from 'i18n';
import { map } from 'ramda';
import { useParams } from 'react-router-dom';
import {
  ProjectScenarioDto,
  useCloneScenarioMutation,
  useDeleteScenarioMutation,
  useScenariosQuery
} from 'providers/api';
import { trackEvent } from 'providers/analytics';
import { ProjectAreaPageParams } from 'modules/project/ProjectAreaPage/constants';
import ModelDrawer from '../../ModelDrawer';
import { navigateToScene, SceneProps } from '../scene-utils';
import { Model } from '../../hooks/useModelState';
import ScenarioSidebar, { ScenarioListItem } from './ScenarioSidebar';

const mapDtoToListItem = (dto: ProjectScenarioDto): ScenarioListItem => ({
  scenarioId: dto.scenarioId ?? '',
  name: dto.name ?? '',
  clonedFromScenarioId: dto.clonedFromScenarioId ?? null,
  createdAt: dto.createdAt ?? '',
  createdBy: dto.createdBy ?? '',
});

interface ScenarioManagerSceneSideDrawerProps {
  props: SceneProps;
  model: Model;
}

const ScenarioManagerSceneSideDrawer = ({ props, model }: ScenarioManagerSceneSideDrawerProps) => {
  const params = useParams<ProjectAreaPageParams>();
  const mineModelId = params.entityId ?? '';
  const { drawerOpen } = props.context.state;
  const { setDrawerOpen } = props.context.actions;

  const { data: scenarios = [], isLoading } = useScenariosQuery({ mineModelId });
  const cloneMutation = useCloneScenarioMutation();
  const deleteMutation = useDeleteScenarioMutation();

  React.useEffect(() => {
    if (!drawerOpen) {
      setDrawerOpen(true);
    }
  }, [drawerOpen, setDrawerOpen]);

  const onClone = async (sourceScenarioId: string | null, name: string, cloneRecipes: boolean, clonePours: boolean) => {
    await cloneMutation.mutateAsync({
      mineModelId,
      form: { name, sourceScenarioId: sourceScenarioId ?? undefined, cloneRecipes, clonePours },
    });
    trackEvent('scenario_created', { project_id: mineModelId, scenario_name: name });
  };

  const onDelete = async (scenarioId: string) => {
    await deleteMutation.mutateAsync({ mineModelId, scenarioId });
  };

  const handleBack = () => {
    navigateToScene('DEFAULT', { model, context: props.context });
  };

  const listItems = map(mapDtoToListItem, scenarios);

  return (
    <ModelDrawer
      backNavText={i18n._('Stopes')}
      isOpen={drawerOpen}
      setIsOpen={setDrawerOpen}
      onBack={handleBack}
    >
      <ScenarioSidebar
        scenarios={listItems}
        isLoading={isLoading}
        onClone={onClone}
        onDelete={onDelete}
      />
    </ModelDrawer>
  );
};

const sideDrawerContent = (
  props: SceneProps,
  model: Model,
): JSX.Element => <ScenarioManagerSceneSideDrawer props={props} model={model} />;

export default sideDrawerContent;
